import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { EmotionProjectReply } from "~/app/textToSpeech/project.service";
import {
  EMOTION_CANONICAL_MAP,
  EMOTION_CATEGORY_KEYS,
  EMOTION_SUGGESTION_MAP,
  OTHER_TAG_CATEGORIES,
  OtherTagCategory,
} from "./emotion-tags.model";

@Component({
  selector: "app-emotion-reply",
  templateUrl: "./emotion-reply.component.html",
  styleUrls: ["./emotion-reply.component.scss"],
})
export class EmotionReplyComponent implements OnChanges {
  @Input() reply: EmotionProjectReply;
  @Input() index: number;
  @Input() editableDialogue = false;
  @Input() showEmotionLabels = false;
  @Input() isSaving = false;
  @Input() saveFailed = false;
  @Input() showSavedIndicator = false;
  @Input() playbackManagedExternally = false;
  @Input() externalIsPlaying = false;
  @Input() externalIsPlayingOriginal = false;
  @Input() playEnabled = true;
  @Input() isRegeneratingAudio = false;
  @Input() originalPlayEnabled = false;
  @Input() stereoMixUrl: string | null = null;
  @Input() showOriginalAudioButton = false;
  @Input() formatSecondsToSmpte?: (sec: number) => string;
  @Input() projectStyleTransferEnabled = false;
  @Input() isChangingSpeed = false;
  @Input() showReplyControls = false;
  @Output() saveDialogue = new EventEmitter<string>();
  @Output() playRequested = new EventEmitter<void>();
  @Output() playOriginalRequested = new EventEmitter<void>();
  @Output() styleTransferChanged = new EventEmitter<boolean>();
  @Output() radioEffectChanged = new EventEmitter<boolean>();
  @Output() speedRateChanged = new EventEmitter<number>();
  @ViewChild("dialogueInput")
  private readonly dialogueInputRef?: ElementRef<HTMLDivElement>;

  private currentAudio: HTMLAudioElement | null = null;
  private localSegmentTimeUpdate: (() => void) | null = null;
  private suppressNextBlurSave = false;
  // Always appended to the played audio URL. The main/original wav files are re-uploaded
  // in-place at the same S3 key on every regeneration or speed change, so a "one-shot"
  // cache buster (reset to null after first use) is not enough: any later play would fall
  // back to the bare URL, which the browser may have cached under a stale response from
  // *before* this bust — a completely separate cache entry from the busted one. Keeping a
  // persistent, ever-changing value and always using it avoids ever hitting that stale entry.
  private audioCacheBuster: number = Date.now();
  private lastSelectionStart: number | null = null;
  private lastSelectionEnd: number | null = null;
  private previousSpeedRate: number | null = null;
  private previousReplyId: number | null = null;
  public static readonly SPEED_RATE_MIN = 0.5;
  public static readonly SPEED_RATE_MAX = 2.0;
  public isPlaying = false;
  private isPlayingOriginalLocal = false;
  public dialogueDraft = "";
  public activeEmotionLabel: string | null = null;
  public dialogueSegments: Array<{
    text: string;
    emotion: string | null;
    tagClass: string;
  }> = [];
  public translatedSegments: Array<{ type: "text" | "tag"; value: string }> =
    [];
  public otherTagsExpanded = false;
  public activeOtherCategory: string | null = null;
  public readonly otherTagCategories: readonly OtherTagCategory[] =
    OTHER_TAG_CATEGORIES;

  constructor(private readonly cdr: ChangeDetectorRef) {}

  public get playing(): boolean {
    return this.playbackManagedExternally
      ? this.externalIsPlaying
      : this.isPlaying;
  }

  public get playingOriginal(): boolean {
    return this.playbackManagedExternally
      ? this.externalIsPlayingOriginal
      : this.isPlayingOriginalLocal;
  }

  public get formattedCount(): string {
    const displayIndex = Number(this.index) + 1;
    return displayIndex < 10 ? `0${displayIndex}` : `${displayIndex}`;
  }

  public get styleTransferValue(): boolean {
    return this.reply?.styleTransferEnabled ?? this.projectStyleTransferEnabled;
  }

  public onStyleTransferChange(value: boolean): void {
    if (value === this.styleTransferValue) {
      return;
    }
    this.styleTransferChanged.emit(value);
  }

  public get radioEffectValue(): boolean {
    return (
      this.reply?.radioEffectEnabled ?? this.reply?.radioEffectDetected ?? false
    );
  }

  public onRadioEffectChange(value: boolean): void {
    if (value === this.radioEffectValue) {
      return;
    }
    this.radioEffectChanged.emit(value);
  }

  public onSpeedRateChange(): void {
    if (this.isChangingSpeed || !this.reply || this.reply.speedRate == null) {
      return;
    }

    const clamped = Math.min(
      EmotionReplyComponent.SPEED_RATE_MAX,
      Math.max(
        EmotionReplyComponent.SPEED_RATE_MIN,
        Number(this.reply.speedRate)
      )
    );
    this.reply.speedRate = Number(clamped.toFixed(2));

    if (this.reply.speedRate === this.previousSpeedRate) {
      return;
    }

    this.previousSpeedRate = this.reply.speedRate;
    this.speedRateChanged.emit(this.reply.speedRate);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.reply) {
      this.dialogueDraft =
        this.reply?.translatedDialogue ?? this.reply?.dialogue ?? "";
      this.activeEmotionLabel = null;
      this.lastSelectionStart = null;
      this.lastSelectionEnd = null;
      this.dialogueSegments = this.buildDialogueSegments();
      this.translatedSegments = this.buildTranslatedSegments(
        this.dialogueDraft
      );
      if (this.previousReplyId !== this.reply?.id) {
        this.previousReplyId = this.reply?.id ?? null;
        this.previousSpeedRate = this.reply?.speedRate ?? 1;
      }
    }
    if (changes.isRegeneratingAudio) {
      const wasRegenerating =
        changes.isRegeneratingAudio.previousValue === true;
      const isDone = changes.isRegeneratingAudio.currentValue === false;
      if (wasRegenerating && isDone) {
        this.audioCacheBuster = Date.now();
      }
    }
    if (changes.isChangingSpeed) {
      const wasChangingSpeed = changes.isChangingSpeed.previousValue === true;
      const isDone = changes.isChangingSpeed.currentValue === false;
      if (wasChangingSpeed && isDone) {
        this.audioCacheBuster = Date.now();
      }
    }
  }

  public onDialogueRichInput(event: Event): void {
    if (this.isRegeneratingAudio) {
      return;
    }
    const target = event.target as HTMLElement;
    // While the user is actively typing, we deliberately leave the rich
    // (chip) rendering untouched and let the browser handle the raw text
    // natively: re-rendering a tag into a chip mid-typing requires moving
    // the caret across a newly-inserted non-editable widget, which is
    // fragile (focus can end up landing on the chip and swallow the next
    // keystroke). Manually-typed tags are only turned into chips once the
    // field loses focus (see onDialogueBlur) or on Enter/paste. Tags
    // inserted via the suggestion buttons still render immediately, since
    // that flow doesn't fight with an in-progress keystroke.
    this.dialogueDraft = this.elementToDialogueString(target);
    this.captureSelection();
  }

  public onDialogueSelectionChange(_event: Event): void {
    this.captureSelection();
  }

  public onDialoguePaste(event: ClipboardEvent): void {
    if (!this.editableDialogue || this.isRegeneratingAudio) {
      return;
    }
    event.preventDefault();
    const text = event.clipboardData?.getData("text/plain") ?? "";
    if (!text) {
      return;
    }
    document.execCommand("insertText", false, text);

    const target = this.dialogueInputRef?.nativeElement;
    if (target) {
      this.dialogueDraft = this.elementToDialogueString(target);
    }
    const range = this.getSelectionStringRange();
    this.resyncTranslatedSegments(this.dialogueDraft);
    if (range) {
      this.restoreCaret(range.end);
    }
  }

  public onRemoveTranslatedTag(segment: {
    type: "text" | "tag";
    value: string;
  }): void {
    if (!this.editableDialogue || this.isRegeneratingAudio) {
      return;
    }
    const index = this.translatedSegments.indexOf(segment);
    if (index < 0) {
      return;
    }
    this.dialogueDraft = this.translatedSegments
      .filter((_, i) => i !== index)
      .map((seg) => seg.value)
      .join("");
    this.resyncTranslatedSegments(this.dialogueDraft);
    this.suppressNextBlurSave = false;
    this.saveDraft();
  }

  public get hasDialogueChanges(): boolean {
    return (
      `${this.dialogueDraft}` !==
      `${this.reply?.translatedDialogue ?? this.reply?.dialogue ?? ""}`
    );
  }

  public get sentenceEmotionChips(): Array<{ text: string; emotion: string }> {
    const sentences = this.reply?.sentenceEmotions ?? [];
    return sentences
      .filter((sentence) => !!`${sentence?.emotion ?? ""}`.trim())
      .map((sentence) => ({
        text: `${sentence?.text ?? ""}`.trim(),
        emotion: `${sentence?.emotion ?? ""}`.trim(),
      }));
  }

  public get allEmotionChips(): string[] {
    const allKeys = Object.keys(EMOTION_SUGGESTION_MAP);
    const seen = new Set<string>();
    const ordered: string[] = [];
    for (const chip of this.sentenceEmotionChips) {
      const key = this.normalizeEmotionKey(chip.emotion);
      if (key && allKeys.includes(key) && !seen.has(key)) {
        seen.add(key);
        ordered.push(key);
      }
    }
    for (const key of allKeys) {
      if (!seen.has(key)) {
        ordered.push(key);
      }
    }
    return ordered;
  }

  public get hasInlineEmotions(): boolean {
    return this.dialogueSegments.some((segment) => !!segment.emotion);
  }

  public trackTranslatedSegment(index: number): number {
    return index;
  }

  public getTagLabel(value: string): string {
    const trimmed = `${value ?? ""}`.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      return trimmed.slice(1, -1);
    }
    return trimmed;
  }

  public getTranslatedTagClass(value: string): string {
    const label = this.getTagLabel(value);
    const canonical = this.normalizeEmotionKey(label);
    if (EMOTION_CATEGORY_KEYS.includes(canonical)) {
      return this.getEmotionTagClass(canonical);
    }
    return this.getEmotionTagClass("neutral");
  }

  public buildTranslatedSegments(
    text: string
  ): Array<{ type: "text" | "tag"; value: string }> {
    const source = `${text ?? ""}`;
    if (!source) {
      return [];
    }

    const segments: Array<{ type: "text" | "tag"; value: string }> = [];
    const pattern = /\[[^\]]+\]/g;
    let cursor = 0;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(source)) !== null) {
      const matchStart = match.index;
      const matchEnd = match.index + match[0].length;

      if (matchStart > cursor) {
        segments.push({
          type: "text",
          value: source.slice(cursor, matchStart),
        });
      } else if (
        !segments.length ||
        segments[segments.length - 1].type === "tag"
      ) {
        // A chip is a non-editable "island" inside the contenteditable
        // area. Without a real (even empty) text node right before it,
        // the browser has no anchor to place the caret in, which makes
        // typing right at the start of the field, or between two
        // adjacent chips, behave erratically (e.g. characters appearing
        // to spawn extra chips instead of just being inserted as text).
        segments.push({ type: "text", value: "" });
      }
      segments.push({ type: "tag", value: match[0] });
      cursor = matchEnd;
    }
    if (cursor < source.length) {
      segments.push({ type: "text", value: source.slice(cursor) });
    } else if (
      segments.length &&
      segments[segments.length - 1].type === "tag"
    ) {
      // Same anchor issue as above, but for typing right after the last
      // chip in the field (the most common case when a freshly-typed
      // tag turns into a chip).
      segments.push({ type: "text", value: "" });
    }

    return segments;
  }

  /**
   * Rebuilds `translatedSegments` from `text` and makes sure the editable
   * DOM ends up exactly matching that new decorated content.
   *
   * While the user types, the contenteditable area is left to the browser:
   * raw characters (including freshly-typed `[tags]`) are inserted as plain
   * DOM text nodes that Angular's `*ngFor` over `translatedSegments` has no
   * knowledge of. When we later rebuild `translatedSegments` (on blur,
   * Enter, paste, or after inserting/removing a tag) to turn those tags
   * into chips, simply reassigning the array is not enough: Angular's
   * `*ngFor` only knows how to update/remove the DOM nodes *it* created,
   * so any raw text the browser inserted on its own is left behind
   * untouched, appearing alongside the newly rendered chip instead of
   * being replaced by it. We force a clean re-render by first letting
   * Angular tear down its own previous nodes (via a synchronous
   * `detectChanges()`), then wiping out whatever is left in the DOM
   * (necessarily browser-inserted content Angular never owned) before
   * rendering the freshly built segments into that now-empty container.
   */
  private resyncTranslatedSegments(text: string): void {
    this.translatedSegments = [];
    this.cdr.detectChanges();
    const container = this.dialogueInputRef?.nativeElement;
    if (container) {
      // `*ngFor`/`*ngIf` anchor their views on comment nodes (to know
      // where to insert future content) which stick around even when
      // there are zero items. Only strip actual content nodes here -
      // removing those comment anchors would leave Angular unable to
      // properly re-insert the freshly rebuilt segments afterwards.
      Array.from(container.childNodes)
        .filter((node) => node.nodeType !== Node.COMMENT_NODE)
        .forEach((node) => container.removeChild(node));
    }
    this.translatedSegments = this.buildTranslatedSegments(text);
  }

  private buildDialogueSegments(): Array<{
    text: string;
    emotion: string | null;
    tagClass: string;
  }> {
    const source = `${this.reply?.dialogue ?? ""}`;
    const sentences = this.sentenceEmotionChips;

    if (!source || !sentences.length) {
      return [{ text: source, emotion: null, tagClass: "" }];
    }

    const sourceTokens = this.tokenizeWithOffsets(source);
    if (!sourceTokens.length) {
      return [{ text: source, emotion: null, tagClass: "" }];
    }
    const starts: Array<{ tokenIdx: number; emotion: string }> = [];
    let searchFrom = 0;
    for (const sentence of sentences) {
      const sentenceWords = this.normalizeWords(sentence.text);
      if (!sentenceWords.length) {
        continue;
      }
      const tokenIdx = this.findSentenceStart(
        sourceTokens,
        sentenceWords,
        searchFrom
      );
      if (tokenIdx < 0) {
        continue;
      }
      starts.push({ tokenIdx, emotion: sentence.emotion });
      searchFrom = tokenIdx + 1;
    }

    const ranges: Array<{ start: number; end: number; emotion: string }> =
      starts.map((entry, index) => {
        const endTokenIdx =
          index + 1 < starts.length
            ? starts[index + 1].tokenIdx - 1
            : sourceTokens.length - 1;
        const safeEndIdx = Math.max(entry.tokenIdx, endTokenIdx);
        return {
          start: sourceTokens[entry.tokenIdx].start,
          end: sourceTokens[safeEndIdx].end,
          emotion: entry.emotion,
        };
      });

    if (!ranges.length) {
      return [{ text: source, emotion: null, tagClass: "" }];
    }

    const segments: Array<{
      text: string;
      emotion: string | null;
      tagClass: string;
    }> = [];
    let cursor = 0;
    for (const range of ranges) {
      if (range.start > cursor) {
        segments.push({
          text: source.slice(cursor, range.start),
          emotion: null,
          tagClass: "",
        });
      }
      segments.push({
        text: source.slice(range.start, range.end),
        emotion: range.emotion,
        tagClass: this.getEmotionTagClass(range.emotion),
      });
      cursor = range.end;
    }
    if (cursor < source.length) {
      segments.push({
        text: source.slice(cursor),
        emotion: null,
        tagClass: "",
      });
    }

    return segments;
  }

  private findSentenceStart(
    tokens: Array<{ word: string; start: number; end: number }>,
    words: string[],
    from: number
  ): number {
    let best = -1;
    let bestScore = -1;
    const probes = Math.min(3, words.length);
    for (let p = 0; p < probes; p++) {
      for (let i = from; i < tokens.length; i++) {
        if (tokens[i].word !== words[p]) {
          continue;
        }
        let score = 0;
        let cursor = i + 1;
        let wordIdx = p + 1;
        let look = 0;
        while (cursor < tokens.length && wordIdx < words.length && look < 8) {
          if (tokens[cursor].word === words[wordIdx]) {
            score++;
            wordIdx++;
          }
          cursor++;
          look++;
        }
        const anchor = Math.max(from, i - p);
        if (score > bestScore) {
          bestScore = score;
          best = anchor;
        }
        break;
      }
    }
    return best;
  }

  private tokenizeWithOffsets(
    source: string
  ): Array<{ word: string; start: number; end: number }> {
    const tokens: Array<{ word: string; start: number; end: number }> = [];
    const regex = /\S+/g;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(source)) !== null) {
      const normalized = this.normalizeWord(match[0]);
      if (!normalized) {
        continue;
      }
      tokens.push({
        word: normalized,
        start: match.index,
        end: match.index + match[0].length,
      });
    }
    return tokens;
  }

  private normalizeWords(text: string): string[] {
    return `${text ?? ""}`
      .split(/\s+/g)
      .map((word) => this.normalizeWord(word))
      .filter((word) => !!word);
  }

  private normalizeWord(word: string): string {
    return `${word ?? ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");
  }

  public get emotionSuggestions(): string[] {
    const key = this.normalizeEmotionKey(this.activeEmotionLabel);
    if (!key) {
      return [];
    }
    return EMOTION_SUGGESTION_MAP[key] ?? [];
  }

  public getEmotionTagClass(label: string): string {
    const normalized = `${label || ""}`.trim().toLowerCase();

    if (
      ["happy", "joy", "excited", "love", "optimistic"].includes(normalized)
    ) {
      return "tag-deliver";
    }
    if (["sad", "grief", "melancholy"].includes(normalized)) {
      return "tag-share";
    }
    if (["angry", "anger", "frustrated", "furious"].includes(normalized)) {
      return "tag-publish";
    }
    if (
      ["fear", "fearful", "scared", "anxious", "nervous"].includes(normalized)
    ) {
      return "tag-archive";
    }
    if (["surprised", "surprise"].includes(normalized)) {
      return "tag-find";
    }
    if (["disgust", "disgusted"].includes(normalized)) {
      return "tag-admin";
    }
    if (["neutral", "calm", "regular"].includes(normalized)) {
      return "tag-regular";
    }

    return "tag-find";
  }

  public saveDraft(): void {
    if (!this.hasDialogueChanges || this.isSaving || this.isRegeneratingAudio) {
      return;
    }
    this.saveDialogue.emit(this.dialogueDraft);
  }

  public onEmotionTagMouseDown(): void {
    this.suppressNextBlurSave = true;
  }

  public onEmotionLabelClick(label: string): void {
    if (!this.editableDialogue || this.isRegeneratingAudio) {
      return;
    }

    this.activeEmotionLabel = this.activeEmotionLabel === label ? null : label;
  }

  public onEmotionSuggestionClick(suggestion: string): void {
    if (!this.editableDialogue || this.isRegeneratingAudio) {
      return;
    }

    this.dialogueDraft = this.insertSuggestionAtSelection(
      this.dialogueDraft,
      suggestion
    );
    this.resyncTranslatedSegments(this.dialogueDraft);
    this.saveDraft();
  }

  public onToggleOtherTags(): void {
    if (!this.editableDialogue || this.isRegeneratingAudio) {
      return;
    }
    this.otherTagsExpanded = !this.otherTagsExpanded;
    if (!this.otherTagsExpanded) {
      this.activeOtherCategory = null;
    }
  }

  public onOtherCategoryClick(key: string): void {
    if (!this.editableDialogue || this.isRegeneratingAudio) {
      return;
    }
    this.activeOtherCategory = this.activeOtherCategory === key ? null : key;
  }

  public get activeOtherTags(): string[] {
    const found = this.otherTagCategories.find(
      (category) => category.key === this.activeOtherCategory
    );
    return found ? found.tags : [];
  }

  public onOtherTagClick(tag: string): void {
    if (!this.editableDialogue || this.isRegeneratingAudio) {
      return;
    }

    this.dialogueDraft = this.insertSuggestionAtSelection(
      this.dialogueDraft,
      tag
    );
    this.resyncTranslatedSegments(this.dialogueDraft);
    this.saveDraft();
  }

  public onDialogueBlur(): void {
    if (!this.editableDialogue || this.isRegeneratingAudio) {
      return;
    }
    this.resyncTranslatedSegments(this.dialogueDraft);
    if (this.suppressNextBlurSave) {
      this.suppressNextBlurSave = false;
      return;
    }
    this.saveDraft();
  }

  public onDialogueKeydown(event: KeyboardEvent): void {
    if (
      !this.editableDialogue ||
      this.isRegeneratingAudio ||
      event.key !== "Enter" ||
      event.shiftKey
    ) {
      return;
    }
    event.preventDefault();
    this.suppressNextBlurSave = true;
    this.resyncTranslatedSegments(this.dialogueDraft);
    this.saveDraft();
  }

  public togglePlay(): void {
    if (this.playbackManagedExternally) {
      if (!this.playEnabled) {
        return;
      }
      this.playRequested.emit();
      return;
    }

    if (!this.reply?.audioFilePath) {
      return;
    }

    if (this.isPlaying && this.currentAudio) {
      this.stopLocalPlayback();
      return;
    }

    this.stopLocalPlayback();
    const audioUrl = `${this.reply.audioFilePath}?t=${this.audioCacheBuster}`;
    this.currentAudio = new Audio(audioUrl);
    this.isPlaying = true;
    this.currentAudio.addEventListener("ended", () => this.stopLocalPlayback());
    this.currentAudio.addEventListener("error", () => this.stopLocalPlayback());
    this.currentAudio.play().catch(() => this.stopLocalPlayback());
  }

  public togglePlayOriginal(): void {
    if (this.playbackManagedExternally) {
      if (!this.originalPlayEnabled) {
        return;
      }
      this.playOriginalRequested.emit();
      return;
    }

    const stereo = `${this.stereoMixUrl ?? ""}`.trim();
    if (!stereo || !this.reply) {
      return;
    }

    const start = Number(this.reply.startSec) || 0;
    const end = Number(this.reply.endSec) || 0;
    if (end <= start + 0.01) {
      return;
    }

    if (this.isPlayingOriginalLocal && this.currentAudio) {
      this.stopLocalPlayback();
      return;
    }

    this.stopLocalPlayback();
    const audio = new Audio(stereo);
    this.currentAudio = audio;
    this.isPlayingOriginalLocal = true;

    this.localSegmentTimeUpdate = () => {
      if (audio.currentTime >= end - 0.02) {
        audio.pause();
        this.stopLocalPlayback();
      }
    };

    const startPlayback = () => {
      try {
        audio.currentTime = start;
      } catch {
        // ignore seek errors
      }
      if (this.localSegmentTimeUpdate) {
        audio.addEventListener("timeupdate", this.localSegmentTimeUpdate);
      }
      audio.play().catch(() => this.stopLocalPlayback());
    };

    audio.addEventListener("ended", () => this.stopLocalPlayback());
    audio.addEventListener("error", () => this.stopLocalPlayback());

    if (audio.readyState >= 1) {
      startPlayback();
    } else {
      audio.addEventListener("loadeddata", startPlayback, { once: true });
    }
  }

  private stopLocalPlayback(): void {
    if (this.currentAudio && this.localSegmentTimeUpdate) {
      this.currentAudio.removeEventListener(
        "timeupdate",
        this.localSegmentTimeUpdate
      );
    }
    this.localSegmentTimeUpdate = null;
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    this.isPlaying = false;
    this.isPlayingOriginalLocal = false;
  }

  private normalizeEmotionKey(label: string | null): string {
    const normalized = `${label || ""}`.trim().toLowerCase();
    if (!normalized) {
      return "";
    }
    return EMOTION_CANONICAL_MAP[normalized] ?? normalized;
  }

  private captureSelection(): void {
    const range = this.getSelectionStringRange();
    this.lastSelectionStart = range ? range.start : null;
    this.lastSelectionEnd = range ? range.end : null;
  }

  private insertSuggestionAtSelection(
    dialogue: string,
    suggestion: string
  ): string {
    const currentDialogue = `${dialogue ?? ""}`;
    const cue = `${suggestion ?? ""}`.trim();

    if (!cue) {
      return currentDialogue;
    }
    if (!currentDialogue) {
      const nextCaret = cue.length;
      this.lastSelectionStart = nextCaret;
      this.lastSelectionEnd = nextCaret;
      this.restoreCaret(nextCaret);
      return cue;
    }

    const canUseSelection =
      this.lastSelectionStart !== null &&
      this.lastSelectionEnd !== null &&
      this.lastSelectionStart >= 0 &&
      this.lastSelectionEnd >= this.lastSelectionStart &&
      this.lastSelectionEnd <= currentDialogue.length;

    // Default to inserting at the very start when there is no usable
    // caret position, matching the previous "prepend" fallback behavior.
    const selectionStart = canUseSelection
      ? (this.lastSelectionStart as number)
      : 0;
    const selectionEnd = canUseSelection
      ? (this.lastSelectionEnd as number)
      : 0;

    const before = currentDialogue.slice(0, selectionStart);
    const after = currentDialogue.slice(selectionEnd);

    // Always keep the inserted tag isolated by whitespace on both sides.
    // Without this, clicking several tag suggestions in a row (e.g. from
    // the "Others" panel) without refocusing the text field would glue
    // them together (e.g. "[emphasized][laughs]"), which ElevenLabs does
    // not parse as two separate audio directives.
    const needsLeadingSpace = before.length > 0 && !/\s$/.test(before);
    const needsTrailingSpace = after.length > 0 && !/^\s/.test(after);
    const insertion = `${needsLeadingSpace ? " " : ""}${cue}${
      needsTrailingSpace ? " " : ""
    }`;

    const updated = `${before}${insertion}${after}`;
    const nextCaret = before.length + insertion.length;
    this.lastSelectionStart = nextCaret;
    this.lastSelectionEnd = nextCaret;
    this.restoreCaret(nextCaret);
    return updated;
  }

  private restoreCaret(position: number): void {
    if (!this.dialogueInputRef?.nativeElement) {
      return;
    }
    setTimeout(() => {
      const editor = this.dialogueInputRef?.nativeElement;
      if (!editor) {
        return;
      }
      editor.focus();
      this.setCaretAtStringIndex(editor, position);
    });
  }

  private elementToDialogueString(container: Node): string {
    let result = "";
    container.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        result += child.textContent ?? "";
        return;
      }
      if (child.nodeType !== Node.ELEMENT_NODE) {
        return;
      }
      const element = child as HTMLElement;
      if (element.tagName === "BR") {
        result += "\n";
        return;
      }
      const tagValue = element.getAttribute("data-tag");
      if (tagValue !== null) {
        result += tagValue;
        return;
      }
      result += this.elementToDialogueString(element);
      if (element.tagName === "DIV" || element.tagName === "P") {
        result += "\n";
      }
    });
    return result;
  }

  private nodeStringLength(node: Node): number {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent ?? "").length;
    }
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as HTMLElement;
      if (element.tagName === "BR") {
        return 1;
      }
      const tagValue = element.getAttribute("data-tag");
      if (tagValue !== null) {
        return tagValue.length;
      }
    }
    let total = 0;
    node.childNodes.forEach((child) => {
      total += this.nodeStringLength(child);
    });
    return total;
  }

  private getSelectionStringRange(): { start: number; end: number } | null {
    const container = this.dialogueInputRef?.nativeElement ?? null;
    const selection = window.getSelection ? window.getSelection() : null;
    if (!container || !selection || selection.rangeCount === 0) {
      return null;
    }
    const range = selection.getRangeAt(0);
    if (
      !container.contains(range.startContainer) ||
      !container.contains(range.endContainer)
    ) {
      return null;
    }
    const start = this.pointToStringIndex(
      container,
      range.startContainer,
      range.startOffset
    );
    const end = this.pointToStringIndex(
      container,
      range.endContainer,
      range.endOffset
    );
    if (start === null || end === null) {
      return null;
    }
    return start <= end ? { start, end } : { start: end, end: start };
  }

  private pointToStringIndex(
    container: HTMLElement,
    node: Node,
    offset: number
  ): number | null {
    if (node === container) {
      let index = 0;
      const children = Array.from(container.childNodes).slice(0, offset);
      for (const child of children) {
        index += this.nodeStringLength(child);
      }
      return index;
    }

    let topLevel: Node | null = node;
    while (topLevel && topLevel.parentNode !== container) {
      topLevel = topLevel.parentNode;
    }
    if (!topLevel) {
      return null;
    }

    let index = 0;
    for (const sibling of Array.from(container.childNodes)) {
      if (sibling === topLevel) {
        break;
      }
      index += this.nodeStringLength(sibling);
    }

    if (node.nodeType === Node.TEXT_NODE) {
      index += offset;
    } else if (node === topLevel) {
      const children = Array.from(topLevel.childNodes).slice(0, offset);
      for (const child of children) {
        index += this.nodeStringLength(child);
      }
    }
    return index;
  }

  private getContentChildNodes(container: Node): Node[] {
    return Array.from(container.childNodes).filter(
      (node) =>
        node.nodeType === Node.TEXT_NODE || node.nodeType === Node.ELEMENT_NODE
    );
  }

  private setCaretAtStringIndex(container: HTMLElement, index: number): void {
    const selection = window.getSelection ? window.getSelection() : null;
    if (!selection) {
      return;
    }

    const contentNodes = this.getContentChildNodes(container);
    let remaining = Math.max(0, index);
    const domRange = document.createRange();

    try {
      for (const node of contentNodes) {
        const length = this.nodeStringLength(node);

        if (node.nodeType === Node.TEXT_NODE) {
          if (remaining <= length) {
            domRange.setStart(node, remaining);
            domRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(domRange);
            return;
          }
          remaining -= length;
          continue;
        }

        if (remaining <= 0) {
          domRange.setStartBefore(node);
          domRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(domRange);
          return;
        }
        remaining -= length;
        if (remaining <= 0) {
          domRange.setStartAfter(node);
          domRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(domRange);
          return;
        }
      }

      const lastNode = contentNodes[contentNodes.length - 1];
      if (lastNode) {
        domRange.setStartAfter(lastNode);
      } else {
        domRange.selectNodeContents(container);
      }
      domRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(domRange);
    } catch {}
  }
}
